import { useCallback } from "react";

import { Tooltip } from "antd";
import { selectGeoModelLoading, useMapStore } from "stores/map";

import { LabeledSwitch } from "components/base";
import ColorPaletteSelector from "components/shared/ColorPaletteSelector";
import SegmentedToggle from "components/shared/SegmentedToggle";
import { IColorPalette } from "components/user-settings/models";

import { useIpdbStore } from "../../contexts/IpdbStoreProvider";
import {
  selectColorErrorMsg,
  selectInterpolateColors,
  selectModelSource,
  selectShowControlWells,
  selectShowIpdb,
  selectShowZero
} from "../../contexts/ipdbSelectors";
import { type IpdbState } from "../../contexts/ipdbStore";
import { THREE_D_MODEL, TWO_D_MODEL } from "../Ipdb";
import { IpdbOptionItem, StyledAlert } from "./shared/IpdbStyledComponents";

interface IpdbDisplayOptionsProps {
  selectedColorPalette: IColorPalette | null;
}

enum ColorInterpolation {
  Smooth = "Smooth",
  Discrete = "Discrete"
}

export default function IpdbDisplayOptions({
  selectedColorPalette
}: IpdbDisplayOptionsProps) {
  const showIpdb = useIpdbStore(selectShowIpdb);
  const modelSource = useIpdbStore(selectModelSource);
  const showControlWells = useIpdbStore(selectShowControlWells);
  const showZero = useIpdbStore(selectShowZero);
  const interpolateColors = useIpdbStore(selectInterpolateColors);
  const colorErrorMsg = useIpdbStore(selectColorErrorMsg);
  const updateIpdbState = useIpdbStore((s) => s.update);
  const geoModelLoading = useMapStore(selectGeoModelLoading);

  const setField = useCallback(
    (payload: Partial<IpdbState>) => updateIpdbState(payload),
    [updateIpdbState]
  );

  return (
    <>
      <IpdbOptionItem className="ipdb-option-item alt-title">
        <LabeledSwitch
          testId="show-control-wells-toggle"
          label={{
            value: "Show Control Wells"
          }}
          switch={{
            isChecked:
              showControlWells &&
              showIpdb &&
              (modelSource === THREE_D_MODEL || modelSource === TWO_D_MODEL),
            isDisabled:
              !showIpdb || (modelSource !== THREE_D_MODEL && modelSource !== TWO_D_MODEL),
            onChange: (v: boolean) => setField({ showControlWells: v }),
            size: "large"
          }}
        />
      </IpdbOptionItem>

      <IpdbOptionItem className="ipdb-option-item alt-title">
        <LabeledSwitch
          testId="show-zero-toggle"
          label={{
            value: "Include Zeros",
            tooltip: "Toggle visibility of zero-value data points"
          }}
          switch={{
            isChecked: showIpdb && showZero,
            isDisabled: !showIpdb,
            onChange: (v: boolean) => setField({ showZero: v }),
            size: "large"
          }}
        />
      </IpdbOptionItem>

      <IpdbOptionItem className="ipdb-option-item">
        <label>Colour Interpolation</label>
        <Tooltip
          title={
            geoModelLoading ? "Available once the geo model finishes loading" : undefined
          }>
          <span>
            <SegmentedToggle
              data-testid="color-interpolation-segmented"
              disabled={geoModelLoading}
              value={
                interpolateColors
                  ? ColorInterpolation.Smooth
                  : ColorInterpolation.Discrete
              }
              options={[
                {
                  label: ColorInterpolation.Smooth,
                  value: ColorInterpolation.Smooth
                },
                {
                  label: ColorInterpolation.Discrete,
                  value: ColorInterpolation.Discrete
                }
              ]}
              onChange={(value) =>
                setField({ interpolateColors: value === ColorInterpolation.Smooth })
              }
            />
          </span>
        </Tooltip>
      </IpdbOptionItem>

      <IpdbOptionItem className="ipdb-option-item ipdb-color-palette-selector">
        <label>Colour Palette</label>
        <ColorPaletteSelector
          selectedPalette={selectedColorPalette}
          onPaletteChange={(palette: IColorPalette) => {
            setField({
              ipdbColorPaletteName: palette.name.toLowerCase(),
              reverseColor: !!palette.reverse
            });
          }}
          placement="bottom"
          testId="ipdb-color-palette"
          showReverseButton
        />
      </IpdbOptionItem>

      {colorErrorMsg && <StyledAlert message={colorErrorMsg} type="error" showIcon />}
    </>
  );
}
